import React, { ReactNode, useState } from 'react'
import { useParams } from 'react-router-dom'
import Fuse from 'fuse.js'
import { captureException } from '@sentry/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { DocumentsList } from '../../../../components/society/Documents/DocumentsList'
import {
  ButtonProps,
  ButtonVariant,
} from '../../../../components/common/Button'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import {
  getSocietyPartnerData,
  isUserAdminInSociety,
  societyHasActivatedPartner,
} from '../../../../helpers/society'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { CreateUpdateFolderModal } from '../../../../components/society/Documents/CreateUpdateFolderModal'
import { CreateUpdateDocumentModal } from '../../../../components/society/Documents/CreateUpdateDocumentModal'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../../components/common/Toast/toast-type'
import config from '../../../../config'
import { documentQueries } from '../../../../api/documents'
import { createSocietyFolders } from '../../../../api/partners'
import {
  getSocietyDocuments,
  getRootDocuments,
  getRootDocument,
  getAllSubDocuments,
} from '../../../../state/stores/documents'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { i18nSocietyType } from '../../../../i18n'

export const ManagementDocumentsView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { documentId } = useParams()
  const [searchString, setSearchString] = useState('')
  const [editFolderDocument, setEditFolderDocument] = useState(false)
  const {
    show: showCreateFolderModal,
    open: openCreateFolderModal,
    close: closeCreateFolderModal,
  } = useModal()
  const {
    show: showCreateDocumentModal,
    open: openCreateDocumentModal,
    close: closeCreateDocumentModal,
  } = useModal()
  const { society } = useCurrentSociety()
  const { data: societyDocuments, isLoading: isLoadingDocuments } = useQuery(
    documentQueries.getPerSocietyAndFolder(
      society?._id || '',
      documentId || society?.boardDocumentsRootFolderId || ''
    )
  )
  const { setToastNotification } = useToastNotifications()

  const showBostadsratternaFolderStructureErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        'managementDocumentsView.flashMessage.createBostadsratternaFolderStructureFailure'
      )
    )
  }

  const showBostadsratternaFolderStructureSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        'managementDocumentsView.flashMessage.createBostadsratternaFolderStructureSuccess'
      )
    )
  }
  const {
    mutate: mutateCreateSocietyFolders,
    isPending: createFolderStructureLoading,
  } = useMutation({
    mutationFn: async (params: { societyId: string; partnerId: string }) => {
      return (
        await createSocietyFolders(
          { section: 'board' },
          { societyId: params.societyId, partnerId: params.partnerId }
        )
      ).data.data
    },
    onSuccess: () => {
      showBostadsratternaFolderStructureSuccessMessage()
    },
    onError: (error) => {
      showBostadsratternaFolderStructureErrorMessage()
      captureException(error)
    },
  })

  const rootRoute = 'management:documents'
  const documentRoute = 'management:documents-detail'

  const isAdmin = isUserAdminInSociety(society, userId as string)

  const section = 'board'

  const handleEditModal = (): void => {
    setEditFolderDocument(true)
    openCreateFolderModal()
  }

  const handleCreateModal = (): void => {
    setEditFolderDocument(false)
    openCreateFolderModal()
  }

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  const getRootSubDocuments = (): IDocumentModel[] => {
    const rootDocument = getRootDocument({
      societyDocuments: societyDocuments || [],
      section,
    })

    if (rootDocument) {
      return getAllSubDocuments(societyDocuments || [], rootDocument._id)
    }
    return []
  }

  const getDocuments = (): IDocumentModel[] => {
    return documentId
      ? getSocietyDocuments({
          societyDocuments: societyDocuments || [],
          parentId: documentId,
          section,
        })
      : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        getRootDocuments({
          societyDocuments: societyDocuments || [],
          section,
        })
  }

  const rootSubDocuments = getRootSubDocuments()
  const documents = getDocuments()

  const isRoot = !documentId
  const currentDocument = documentId
    ? societyDocuments?.find((doc) => doc._id === documentId)
    : undefined

  useDocumentTitle(
    isRoot
      ? reverseDocumentTitle('management:documents', {
          '{{ societyName }}': society?.name,
        })
      : reverseDocumentTitle('management:documents-detail', {
          '{{ societyName }}': society?.name,
          '{{ documentName }}': currentDocument?.name,
        })
  )

  const getRootDocumentId = (): string => {
    if (isRoot && documents.length > 0) {
      const firstChild = documents[0]
      const { parentId } = firstChild
      return parentId || ''
    }
    return ''
  }

  const rootDocumentId = getRootDocumentId()

  const fuseOptions = {
    threshold: 0.3,
    keys: ['name'],
  }
  const fuse = new Fuse(documents, fuseOptions)

  const searchDocuments = fuse.search(searchString).map((result) => result.item)

  const loading = isLoadingDocuments

  const errorView =
    !loading && rootSubDocuments.length === 0
      ? {
          title: translate('documentsList.emptyState.title'),
          subtitle: translate(
            'documentsList.emptyState.noDocumentsSubtitleBoard'
          ),
          illustration: IllustrationChoices.CHECKLIST,
        }
      : undefined

  const bostadsratternaSociety = societyHasActivatedPartner(
    society,
    config.BOSTADSRATTERNA_PARTNER_ID
  )

  const bostadsratternaPartnerData = getSocietyPartnerData(
    society,
    config.BOSTADSRATTERNA_PARTNER_ID
  )

  const addBostadsratternaFolderStructure = (): void => {
    if (!society) return
    mutateCreateSocietyFolders({
      societyId: society._id,
      partnerId: config.BOSTADSRATTERNA_PARTNER_ID,
    })
  }

  const getButtons = (): ButtonProps[] => {
    let buttons: ButtonProps[] =
      bostadsratternaSociety &&
      !bostadsratternaPartnerData?.boardFolderSructureCreated
        ? [
            {
              label: translate(
                'managementDocumentsView.bostadsratternaFolderStructure'
              ),
              variant: ButtonVariant.DEFAULT,
              icon: IconChoices.BOSTADSRATTERNA,
              iconSize: 20,
              onClick: () => addBostadsratternaFolderStructure(),
              loading: createFolderStructureLoading,
            },
          ]
        : []
    buttons = [
      ...buttons,
      ...(documentId
        ? [
            {
              label: translate('managementDocumentsView.editFolder'),
              variant: ButtonVariant.DEFAULT,
              icon: IconChoices.EDIT,
              onClick: () => handleEditModal(),
            },
          ]
        : []),
    ]
    buttons = [
      ...buttons,
      {
        label: translate('managementDocumentsView.addNewFolder'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.FOLDER,
        iconSize: 20,
        onClick: () => handleCreateModal(),
      },
      {
        label: translate('managementDocumentsView.addNewDocument'),
        variant: ButtonVariant.PRIMARY,
        icon: IconChoices.DOCUMENT_ADD,
        onClick: () => openCreateDocumentModal(),
      },
    ]
    return isAdmin ? buttons : []
  }

  return (
    <ErrorBoundary>
      <ViewBase
        searchBar={{
          value: searchString,
          onChange: onChangeSearchString,
          placeholder: translate('searchBar.placeholder.search'),
        }}
        title={translate('managementDocumentsView.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        buttons={getButtons()}
        loading={loading}
        errorView={errorView}
      >
        <DocumentsList
          section={section}
          documentId={documentId}
          rootRoute={rootRoute}
          documentRoute={documentRoute}
          rootSubDocuments={rootSubDocuments}
          documents={searchString === '' ? documents : searchDocuments}
          noSearchResults={searchString !== '' && searchDocuments.length === 0}
          document={currentDocument}
        />
      </ViewBase>
      <CreateUpdateFolderModal
        show={showCreateFolderModal}
        close={closeCreateFolderModal}
        parentId={isRoot ? rootDocumentId : documentId}
        section={section}
        id={editFolderDocument ? documentId : undefined}
      />
      <CreateUpdateDocumentModal
        show={showCreateDocumentModal}
        close={closeCreateDocumentModal}
        parentId={isRoot ? rootDocumentId : documentId}
        section={section}
        isBoardDocuments
      />
    </ErrorBoundary>
  )
}
